import React, { useEffect, useState, useCallback } from 'react';
import planchetteIntelService from '../../services/planchetteIntelService';

// ============================================================================
// SEVERITY BADGE
// ============================================================================

const SeverityBadge = ({ severity }) => {
  const colors = {
    urgent: 'bg-red-600 text-white',
    critical: 'bg-red-500 text-white',
    notable: 'bg-yellow-500 text-black',
    info: 'bg-blue-500 text-white',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[severity] || 'bg-gray-500 text-white'}`}>
      {severity}
    </span>
  );
};

const EntityTypeBadge = ({ type }) => {
  const colors = {
    person: 'bg-blue-600',
    organization: 'bg-purple-600',
    property: 'bg-green-600',
    court_case: 'bg-orange-600',
    vehicle: 'bg-gray-600',
    aircraft: 'bg-slate-500',
  };
  const icons = {
    person: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
    organization: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
    property: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
    court_case: 'M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3',
    vehicle: 'M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z',
    aircraft: 'M12 19l9 2-9-18-9 18 9-2zm0 0v-8',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium text-white ${colors[type] || 'bg-gray-500'}`}>
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icons[type] || icons.person} />
      </svg>
      {type}
    </span>
  );
};

// ============================================================================
// STAT CARD
// ============================================================================

const StatCard = ({ label, value, color = 'virgo-teal-400', icon }) => (
  <div className="bg-virgo-navy-800/50 backdrop-blur-sm border border-virgo-navy-600 rounded-lg p-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs text-gray-400 uppercase tracking-wider">{label}</p>
        <p className={`text-2xl font-bold text-${color} mt-1`}>{value ?? '-'}</p>
      </div>
      {icon && (
        <div className={`text-${color} opacity-50`}>
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={icon} />
          </svg>
        </div>
      )}
    </div>
  </div>
);

// ============================================================================
// EMAIL COLLECTOR CARD
// ============================================================================

const EmailCollectorCard = () => {
  const [status, setStatus] = useState(null);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadStatus = async () => {
    try {
      const data = await planchetteIntelService.getEmailCollectorStatus();
      setStatus(data);
      setError(null);
    } catch (err) {
      setError('Unavailable');
    }
  };

  const handlePollNow = async () => {
    try {
      setPolling(true);
      await planchetteIntelService.triggerEmailPoll();
      await loadStatus();
    } catch (err) {
      console.error('Poll failed:', err);
    } finally {
      setPolling(false);
    }
  };

  const statusColor = error ? 'text-red-400' : status?.enabled ? 'text-green-400' : 'text-gray-400';
  const statusText = error ? 'Unavailable' : status?.enabled ? 'Enabled' : 'Disabled';

  return (
    <div className="bg-virgo-navy-800/50 backdrop-blur-sm border border-virgo-navy-600 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-virgo-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <h3 className="text-sm font-medium text-gray-300">Email Collector</h3>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs font-medium ${statusColor}`}>{statusText}</span>
          <button
            onClick={handlePollNow}
            disabled={polling || !status?.enabled}
            className="px-3 py-1 text-xs bg-virgo-teal-600 hover:bg-virgo-teal-500 disabled:opacity-50 text-white rounded font-medium transition-colors"
          >
            {polling ? 'Polling...' : 'Poll Now'}
          </button>
        </div>
      </div>
      {status && (
        <div className="grid grid-cols-4 gap-3">
          <div>
            <p className="text-xs text-gray-500">Last Poll</p>
            <p className="text-sm text-white">
              {status.last_poll ? new Date(status.last_poll).toLocaleTimeString() : 'Never'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Emails Today</p>
            <p className="text-sm text-white">{status.today?.emails_processed || 0}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Entities Found</p>
            <p className="text-sm text-white">{status.today?.entities_created || 0}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Edges Created</p>
            <p className="text-sm text-white">{status.today?.edges_created || 0}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// DASHBOARD TAB
// ============================================================================

const DashboardView = ({ onEntitySelect }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrichName, setEnrichName] = useState('');
  const [enriching, setEnriching] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const data = await planchetteIntelService.getStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load planchette intel stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickEnrich = async (e) => {
    e.preventDefault();
    if (!enrichName.trim()) return;

    const parts = enrichName.trim().split(/\s+/);
    const lastName = parts.pop();
    const firstName = parts.join(' ');

    try {
      setEnriching(true);
      await planchetteIntelService.enrich({ first_name: firstName, last_name: lastName });
      setEnrichName('');
      setTimeout(loadStats, 2000);
    } catch (err) {
      console.error('Enrichment failed:', err);
    } finally {
      setEnriching(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-virgo-teal-400"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Entities"
          value={stats?.total_entities || 0}
          icon="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
        />
        <StatCard
          label="Unreviewed Findings"
          value={stats?.unreviewed_findings || 0}
          color="yellow-400"
          icon="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
        <StatCard
          label="Active Watches"
          value={stats?.active_watches || 0}
          color="blue-400"
          icon="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
        />
        <StatCard
          label="Pending Jobs"
          value={stats?.pending_jobs || 0}
          color="purple-400"
          icon="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
        />
      </div>

      {/* Entity Type Breakdown */}
      {stats?.entities && Object.keys(stats.entities).length > 0 && (
        <div className="bg-virgo-navy-800/50 backdrop-blur-sm border border-virgo-navy-600 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-300 mb-3">Entities by Type</h3>
          <div className="flex flex-wrap gap-4">
            {Object.entries(stats.entities).map(([type, count]) => (
              <div key={type} className="flex items-center gap-2">
                <EntityTypeBadge type={type} />
                <span className="text-white font-medium">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Enrich */}
      <div className="bg-virgo-navy-800/50 backdrop-blur-sm border border-virgo-navy-600 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Quick Enrich</h3>
        <form onSubmit={handleQuickEnrich} className="flex gap-3">
          <input
            type="text"
            value={enrichName}
            onChange={(e) => setEnrichName(e.target.value)}
            placeholder="Enter a person's name (e.g. John Smith)"
            className="flex-1 px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-virgo-teal-500"
          />
          <button
            type="submit"
            disabled={enriching || !enrichName.trim()}
            className="px-6 py-2 bg-virgo-teal-600 hover:bg-virgo-teal-500 disabled:opacity-50 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            {enriching ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            )}
            Enrich
          </button>
        </form>
      </div>


      {/* Email Collector */}
      <EmailCollectorCard />

      {/* Recent Findings */}
      <div className="bg-virgo-navy-800/50 backdrop-blur-sm border border-virgo-navy-600 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Recent Findings</h3>
        {stats?.recent_findings?.length > 0 ? (
          <div className="space-y-2">
            {stats.recent_findings.map((finding) => (
              <div
                key={finding.id}
                className="flex items-center justify-between p-3 bg-virgo-navy-900/50 rounded-lg hover:bg-virgo-navy-900 transition-colors cursor-pointer"
                onClick={() => onEntitySelect(finding.entity_id)}
              >
                <div className="flex items-center gap-3">
                  <SeverityBadge severity={finding.severity} />
                  <span className="text-sm text-white">{finding.title}</span>
                </div>
                <span className="text-xs text-gray-500">
                  {finding.created_at ? new Date(finding.created_at).toLocaleDateString() : ''}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 py-4 text-center">No findings yet. Run an enrichment to discover connections.</p>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// ENTITIES TAB
// ============================================================================

const EntitiesView = ({ onEntitySelect, onShowGraph }) => {
  const [entities, setEntities] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 25;

  const loadEntities = useCallback(async () => {
    try {
      setLoading(true);
      const params = { skip: page * pageSize, limit: pageSize };
      if (search) params.query = search;
      if (typeFilter) params.entity_type = typeFilter;
      const data = await planchetteIntelService.getEntities(params);
      setEntities(data.items);
      setTotal(data.total);
    } catch (err) {
      console.error('Failed to load entities:', err);
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter, page]);

  useEffect(() => {
    loadEntities();
  }, [loadEntities]);

  return (
    <div className="p-6 space-y-4">
      {/* Filters */}
      <div className="flex gap-3">
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          placeholder="Search entities..."
          className="flex-1 px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-virgo-teal-500"
        />
        <select
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
          className="px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-white focus:outline-none focus:border-virgo-teal-500"
        >
          <option value="">All Types</option>
          <option value="person">Person</option>
          <option value="organization">Organization</option>
          <option value="property">Property</option>
          <option value="court_case">Court Case</option>
          <option value="vehicle">Vehicle</option>
          <option value="aircraft">Aircraft</option>
        </select>
      </div>

      {/* Entity List */}
      {loading ? (
        <div className="flex items-center justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-virgo-teal-400"></div>
        </div>
      ) : entities.length === 0 ? (
        <p className="text-center text-gray-500 py-10">No entities found.</p>
      ) : (
        <div className="space-y-2">
          {entities.map((entity) => (
            <div
              key={entity.id}
              className="flex items-center justify-between p-4 bg-virgo-navy-800/50 backdrop-blur-sm border border-virgo-navy-600 rounded-lg hover:border-virgo-teal-600 transition-colors"
            >
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => onEntitySelect(entity.id)}>
                <EntityTypeBadge type={entity.entity_type} />
                <div>
                  <p className="text-white font-medium">{entity.display_name}</p>
                  <p className="text-xs text-gray-500">
                    {entity.contact_id ? 'Linked to contact' : ''}
                    {entity.needs_review && <span className="text-yellow-400 ml-2">Needs review</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {entity.tags?.map((tag) => (
                  <span key={tag} className="px-2 py-0.5 bg-virgo-navy-700 rounded text-xs text-gray-400">{tag}</span>
                ))}
                <button
                  onClick={() => onShowGraph(entity.id)}
                  className="p-2 text-gray-400 hover:text-virgo-teal-400 transition-colors"
                  title="View Graph"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > pageSize && (
        <div className="flex items-center justify-between pt-4">
          <span className="text-sm text-gray-400">
            Showing {page * pageSize + 1}-{Math.min((page + 1) * pageSize, total)} of {total}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="px-3 py-1 bg-virgo-navy-800 border border-virgo-navy-600 rounded text-sm text-gray-300 disabled:opacity-50 hover:border-virgo-teal-600"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={(page + 1) * pageSize >= total}
              className="px-3 py-1 bg-virgo-navy-800 border border-virgo-navy-600 rounded text-sm text-gray-300 disabled:opacity-50 hover:border-virgo-teal-600"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// FINDINGS TAB
// ============================================================================

const FindingsView = ({ onEntitySelect }) => {
  const [findings, setFindings] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [severityFilter, setSeverityFilter] = useState('');
  const [reviewedFilter, setReviewedFilter] = useState('');

  useEffect(() => {
    loadFindings();
  }, [severityFilter, reviewedFilter]);

  const loadFindings = async () => {
    try {
      setLoading(true);
      const params = { limit: 50 };
      if (severityFilter) params.severity = severityFilter;
      if (reviewedFilter !== '') params.reviewed = reviewedFilter === 'true';
      const data = await planchetteIntelService.getFindings(params);
      setFindings(data.items);
      setTotal(data.total);
    } catch (err) {
      console.error('Failed to load findings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (findingId) => {
    try {
      await planchetteIntelService.reviewFinding(findingId);
      loadFindings();
    } catch (err) {
      console.error('Failed to review finding:', err);
    }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex gap-3">
        <select
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value)}
          className="px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-white focus:outline-none focus:border-virgo-teal-500"
        >
          <option value="">All Severities</option>
          <option value="urgent">Urgent</option>
          <option value="critical">Critical</option>
          <option value="notable">Notable</option>
          <option value="info">Info</option>
        </select>
        <select
          value={reviewedFilter}
          onChange={(e) => setReviewedFilter(e.target.value)}
          className="px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-white focus:outline-none focus:border-virgo-teal-500"
        >
          <option value="">All</option>
          <option value="false">Unreviewed</option>
          <option value="true">Reviewed</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-virgo-teal-400"></div>
        </div>
      ) : findings.length === 0 ? (
        <p className="text-center text-gray-500 py-10">No findings match your filters.</p>
      ) : (
        <div className="space-y-2">
          {findings.map((finding) => (
            <div
              key={finding.id}
              className="p-4 bg-virgo-navy-800/50 backdrop-blur-sm border border-virgo-navy-600 rounded-lg"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <SeverityBadge severity={finding.severity} />
                  <div>
                    <p
                      className="text-white font-medium cursor-pointer hover:text-virgo-teal-400"
                      onClick={() => onEntitySelect(finding.entity_id)}
                    >
                      {finding.title}
                    </p>
                    {finding.summary && (
                      <p className="text-sm text-gray-400 mt-1">{finding.summary}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs text-gray-500 bg-virgo-navy-700 px-2 py-0.5 rounded">
                        {finding.finding_type}
                      </span>
                      <span className="text-xs text-gray-500">
                        {finding.created_at ? new Date(finding.created_at).toLocaleString() : ''}
                      </span>
                    </div>
                  </div>
                </div>
                {!finding.reviewed && (
                  <button
                    onClick={() => handleReview(finding.id)}
                    className="px-3 py-1 text-xs bg-virgo-teal-600/20 text-virgo-teal-400 border border-virgo-teal-600/50 rounded hover:bg-virgo-teal-600/40 transition-colors"
                  >
                    Mark Reviewed
                  </button>
                )}
                {finding.reviewed && (
                  <span className="text-xs text-green-400">Reviewed</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// WATCHES TAB
// ============================================================================

const WatchesView = () => {
  const [watches, setWatches] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [watchData, alertData] = await Promise.all([
        planchetteIntelService.getWatches({ active: true }),
        planchetteIntelService.getAlerts({ unread: true, limit: 20 }),
      ]);
      setWatches(watchData.items);
      setAlerts(alertData.items);
    } catch (err) {
      console.error('Failed to load watches:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWatch = async (watchId) => {
    try {
      await planchetteIntelService.deleteWatch(watchId);
      loadData();
    } catch (err) {
      console.error('Failed to delete watch:', err);
    }
  };

  const handleMarkRead = async (alertId) => {
    try {
      await planchetteIntelService.markAlertRead(alertId);
      loadData();
    } catch (err) {
      console.error('Failed to mark alert read:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-virgo-teal-400"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Unread Alerts */}
      {alerts.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-300 mb-3">Unread Alerts ({alerts.length})</h3>
          <div className="space-y-2">
            {alerts.map((alert) => (
              <div key={alert.id} className="flex items-center justify-between p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <div>
                  <p className="text-sm text-white font-medium">{alert.title}</p>
                  {alert.summary && <p className="text-xs text-gray-400 mt-1">{alert.summary}</p>}
                  <p className="text-xs text-gray-500 mt-1">
                    {alert.created_at ? new Date(alert.created_at).toLocaleString() : ''}
                  </p>
                </div>
                <button
                  onClick={() => handleMarkRead(alert.id)}
                  className="px-3 py-1 text-xs text-gray-400 hover:text-white transition-colors"
                >
                  Dismiss
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active Watches */}
      <div>
        <h3 className="text-sm font-medium text-gray-300 mb-3">Active Watches ({watches.length})</h3>
        {watches.length === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center">No active watches. Create one from an entity detail page.</p>
        ) : (
          <div className="space-y-2">
            {watches.map((watch) => (
              <div key={watch.id} className="flex items-center justify-between p-4 bg-virgo-navy-800/50 border border-virgo-navy-600 rounded-lg">
                <div>
                  <p className="text-white font-medium">{watch.entity_name}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-gray-500">
                      Types: {watch.watch_types?.join(', ')}
                    </span>
                    <span className="text-xs text-gray-500">
                      Every {watch.check_interval_hours}h
                    </span>
                    {watch.last_checked && (
                      <span className="text-xs text-gray-500">
                        Last checked: {new Date(watch.last_checked).toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteWatch(watch.id)}
                  className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                  title="Remove Watch"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// MAIN DASHBOARD COMPONENT
// ============================================================================

const PlanchetteIntelDashboard = ({ activeTab, onEntitySelect, onShowGraph }) => {
  switch (activeTab) {
    case 'entities':
      return <EntitiesView onEntitySelect={onEntitySelect} onShowGraph={onShowGraph} />;
    case 'findings':
      return <FindingsView onEntitySelect={onEntitySelect} />;
    case 'watches':
      return <WatchesView />;
    default:
      return <DashboardView onEntitySelect={onEntitySelect} />;
  }
};

export default PlanchetteIntelDashboard;
